/**
 * Credentials slice (PX-W6.5) — the org's credential rows, the project-scoped
 * connector health, and the mutation outcomes.
 *
 * INV-1: NO secret material ever enters this state. The write body
 * (`OutboundCredentialInput.secret`) is built in the page from component-local
 * state, handed to the thunk, dispatched, and forgotten — the fulfilled
 * payload is the allow-list metadata row.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as connectorHealthService from './services/connectorHealthService.http'
import * as credentialsService from './services/credentialsService.http'
import * as outboundOAuthService from './services/outboundOAuthService.http'
import type {
  ConnectorHealthEntry,
  OAuthStartInput,
  OutboundCredentialInput,
  OutboundCredentialMetadata
} from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** The composite key of one credential (provider + subject, or app-only). */
const credentialKey = (provider: string, subject: string | null): string =>
  subject === null || subject === '' ? provider : `${provider}::${subject}`

interface OrgKeyed<T> {
  orgId: string | null
  value: T
}

interface ProjectKeyed<T> {
  key: string
  value: T
}

export const fetchCredentials = createAsyncThunk<
  OrgKeyed<OutboundCredentialMetadata[]>,
  void,
  { rejectValue: ApiError }
>(
  'credentials/fetchCredentials',
  async (_, { getState, rejectWithValue }) => {
    const orgId = orgKey(getState)
    try {
      return { orgId, value: await credentialsService.credentialsService.list() }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { credentials: CredentialsState }).credentials.cache, orgKey(getState))
  }
)

/**
 * Create or rotate a credential. The thunk's arg carries the secret for the
 * lifetime of the dispatch ONLY — the fulfilled payload is the metadata row.
 */
export const saveCredential = createAsyncThunk<
  OutboundCredentialMetadata,
  { provider: string; subject: string | null; input: OutboundCredentialInput },
  { rejectValue: ApiError }
>('credentials/saveCredential', async ({ provider, subject, input }, { rejectWithValue }) => {
  try {
    return await credentialsService.credentialsService.put(provider, subject, input)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/**
 * Revoke a credential. A 404 rejection means the key is already gone — the
 * row is removed anyway (the deleted state, not a hard error; AC-3).
 */
export const revokeCredential = createAsyncThunk<
  OutboundCredentialMetadata | string,
  { provider: string; subject: string | null },
  { rejectValue: ApiError }
>('credentials/revokeCredential', async ({ provider, subject }, { rejectWithValue }) => {
  try {
    return await credentialsService.credentialsService.revoke(provider, subject)
  } catch (error) {
    const apiError = rejected(error)
    if (apiError.status === 404) {
      // The caller names the row to drop via the rejection meta.
      return rejectWithValue({ ...apiError, backendKey: 'credentials.already-absent' })
    }
    return rejectWithValue(apiError)
  }
})

/** Run the health check; the response row carries the fresh stamp. */
export const runHealthCheck = createAsyncThunk<
  OutboundCredentialMetadata,
  { provider: string; subject: string | null },
  { rejectValue: ApiError }
>('credentials/runHealthCheck', async ({ provider, subject }, { rejectWithValue }) => {
  try {
    return await credentialsService.credentialsService.healthCheck(provider, subject)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/**
 * Start a reconnect. On success the browser is sent to the provider's
 * `authorizationUrl` — a one-shot top-level navigation, so it lives in the
 * thunk (I/O belongs in thunks, never reducers). On the (current) 400
 * `outbound-credential.provider.unsupported` the rejection's `backendKey`
 * surfaces as the typed i18n message.
 */
export const startReconnect = createAsyncThunk<
  string,
  { provider: string; body: OAuthStartInput },
  { rejectValue: ApiError }
>('credentials/startReconnect', async ({ provider, body }, { rejectWithValue }) => {
  try {
    const { authorizationUrl } = await outboundOAuthService.outboundOAuthService.reconnect(
      provider,
      body
    )
    if (typeof window !== 'undefined') window.location.assign(authorizationUrl)
    return authorizationUrl
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/** The project-scoped narrow health list. */
export const fetchProjectConnectorHealth = createAsyncThunk<
  ProjectKeyed<ConnectorHealthEntry[]>,
  string,
  { rejectValue: ApiError }
>(
  'credentials/fetchProjectConnectorHealth',
  async (projectId, { getState, rejectWithValue }) => {
    try {
      return {
        key: `${orgKey(getState) ?? 'none'}:${projectId}`,
        value: await connectorHealthService.connectorHealthService.forProject(projectId)
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (projectId, { getState }) =>
      shouldFetch(
        (getState() as { credentials: CredentialsState }).credentials.projectHealthCache,
        `${orgKey(getState) ?? 'none'}:${projectId}`
      )
  }
)

/** The SPA callback completion (AC-4): authenticated XHR, one call. */
export const completeOAuthCallback = createAsyncThunk<
  Awaited<ReturnType<typeof outboundOAuthService.outboundOAuthService.callback>>,
  { code: string; state: string },
  { rejectValue: ApiError }
>('credentials/completeOAuthCallback', async ({ code, state }, { rejectWithValue }) => {
  try {
    return await outboundOAuthService.outboundOAuthService.callback(code, state)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export interface CredentialsState {
  credentials: OutboundCredentialMetadata[]
  cache: FetchCache
  /** The key of the row a mutation is in flight for. */
  inFlightKey: string | null
  error: ApiError | null
  mutationError: ApiError | null
  projectHealth: ConnectorHealthEntry[]
  projectHealthCache: FetchCache
  projectHealthError: ApiError | null
  /** The credential the OAuth callback sealed (rendered by the callback page). */
  sealedCredential: OutboundCredentialMetadata | null
  redirectAfter: string | null
}

const initialState: CredentialsState = {
  credentials: [],
  cache: initialFetchCache,
  inFlightKey: null,
  error: null,
  mutationError: null,
  projectHealth: [],
  projectHealthCache: initialFetchCache,
  projectHealthError: null,
  sealedCredential: null,
  redirectAfter: null
}

/** Replace the row by (provider, subject); append when new. */
const upsertRow = (state: CredentialsState, row: OutboundCredentialMetadata): void => {
  const key = credentialKey(row.provider, row.subject)
  const at = state.credentials.findIndex(
    (existing) => credentialKey(existing.provider, existing.subject) === key
  )
  if (at >= 0) state.credentials[at] = row
  else state.credentials.push(row)
}

export const credentialsSlice = createSlice({
  name: 'credentials',
  initialState,
  reducers: {
    credentialErrorDismissed(state) {
      state.error = null
      state.mutationError = null
    },
    credentialsRetryClicked(state) {
      invalidate(state.cache)
    },
    projectHealthRetryClicked(state) {
      invalidate(state.projectHealthCache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCredentials.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(
        fetchCredentials.fulfilled,
        (state, action: PayloadAction<OrgKeyed<OutboundCredentialMetadata[]>>) => {
          fetchSucceeded(state.cache, action.payload.orgId)
          state.credentials = action.payload.value
        }
      )
      .addCase(fetchCredentials.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(saveCredential.pending, (state, action) => {
        state.inFlightKey = credentialKey(
          action.meta.arg.provider,
          action.meta.arg.subject
        )
        state.mutationError = null
      })
      .addCase(saveCredential.fulfilled, (state, action: PayloadAction<OutboundCredentialMetadata>) => {
        state.inFlightKey = null
        upsertRow(state, action.payload)
      })
      .addCase(saveCredential.rejected, (state, action) => {
        state.inFlightKey = null
        state.mutationError = action.payload ?? null
      })
      .addCase(revokeCredential.pending, (state, action) => {
        state.inFlightKey = credentialKey(
          action.meta.arg.provider,
          action.meta.arg.subject
        )
        state.mutationError = null
      })
      .addCase(revokeCredential.fulfilled, (state, action) => {
        // The server KEEPS the revoked row in the org list (sealed bytes
        // retained for audit; `revokedAt` set, `active` false) — the row
        // dropping from the view is the CLIENT's half of the contract
        // (AC-3). A refetch would bring it back, so no invalidate either.
        state.inFlightKey = null
        const { provider, subject } = action.meta.arg
        state.credentials = state.credentials.filter(
          (row) => credentialKey(row.provider, row.subject) !== credentialKey(provider, subject)
        )
      })
      .addCase(revokeCredential.rejected, (state, action) => {
        state.inFlightKey = null
        // The 404 arm: the row is already gone — drop it, no error surface.
        if (action.payload?.backendKey === 'credentials.already-absent') {
          const { provider, subject } = action.meta.arg
          state.credentials = state.credentials.filter(
            (row) => credentialKey(row.provider, row.subject) !== credentialKey(provider, subject)
          )
          return
        }
        state.mutationError = action.payload ?? null
      })
      .addCase(runHealthCheck.pending, (state, action) => {
        state.inFlightKey = credentialKey(
          action.meta.arg.provider,
          action.meta.arg.subject
        )
        state.mutationError = null
      })
      .addCase(
        runHealthCheck.fulfilled,
        (state, action: PayloadAction<OutboundCredentialMetadata>) => {
          state.inFlightKey = null
          upsertRow(state, action.payload)
        }
      )
      .addCase(runHealthCheck.rejected, (state, action) => {
        state.inFlightKey = null
        state.mutationError = action.payload ?? null
      })
      .addCase(startReconnect.pending, (state) => {
        state.mutationError = null
      })
      .addCase(startReconnect.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
      .addCase(fetchProjectConnectorHealth.pending, (state) => {
        fetchStarted(state.projectHealthCache)
        state.projectHealthError = null
      })
      .addCase(
        fetchProjectConnectorHealth.fulfilled,
        (state, action: PayloadAction<ProjectKeyed<ConnectorHealthEntry[]>>) => {
          fetchSucceeded(state.projectHealthCache, action.payload.key)
          state.projectHealth = action.payload.value
        }
      )
      .addCase(fetchProjectConnectorHealth.rejected, (state, action) => {
        fetchFailed(state.projectHealthCache)
        state.projectHealthError = action.payload ?? null
      })
      .addCase(
        completeOAuthCallback.fulfilled,
        (
          state,
          action: PayloadAction<Awaited<ReturnType<typeof outboundOAuthService.outboundOAuthService.callback>>>
        ) => {
          state.sealedCredential = action.payload.credential
          state.redirectAfter = action.payload.redirectAfter
          state.mutationError = null
        }
      )
      .addCase(completeOAuthCallback.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
  }
})

export const {
  credentialErrorDismissed,
  credentialsRetryClicked,
  projectHealthRetryClicked
} = credentialsSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithCredentials {
  credentials: CredentialsState
}

export const selectCredentials = (s: WithCredentials): OutboundCredentialMetadata[] =>
  s.credentials.credentials
export const selectCredentialsCache = (s: WithCredentials): FetchCache => s.credentials.cache
export const selectCredentialsError = (s: WithCredentials): ApiError | null =>
  s.credentials.error
export const selectCredentialMutationError = (s: WithCredentials): ApiError | null =>
  s.credentials.mutationError
export const selectInFlightKey = (s: WithCredentials): string | null =>
  s.credentials.inFlightKey
export const selectProjectConnectorHealth = (s: WithCredentials): ConnectorHealthEntry[] =>
  s.credentials.projectHealth
export const selectProjectConnectorHealthCache = (s: WithCredentials): FetchCache =>
  s.credentials.projectHealthCache
export const selectProjectConnectorHealthError = (s: WithCredentials): ApiError | null =>
  s.credentials.projectHealthError
export const selectSealedCredential = (s: WithCredentials): OutboundCredentialMetadata | null =>
  s.credentials.sealedCredential
export const selectOAuthRedirectAfter = (s: WithCredentials): string | null =>
  s.credentials.redirectAfter
